/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/browser/KavenBrowserCrypto.ts
 * @create:      2020-07-12 21:17:44.134
 * @modify:      2025-07-03 10:43:33.555
 * @version:     6.0.0
 * @times:       24
 * @lines:       126
 * @copyright:   Copyright © 2020-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { Strings_BEGIN_PRIVATE_KEY, Strings_BEGIN_PUBLIC_KEY, Strings_END_PRIVATE_KEY, Strings_END_PUBLIC_KEY, Strings_LF } from "../const/Strings";
import { String2ArrayBuffer } from "../core/String2ArrayBuffer";
import { ArrayBuffer2String } from "../core/String2ArrayBuffer";

/**
 * RFC 3447 says that "Typical salt lengths" are either 0 or the length of the output of the digest algorithm that was selected when this key was generated. For example, if you use SHA-256 as the digest algorithm, this could be 32.
 * @param keySizeInBits
 * @param digestSizeInBytes
 * @url https://developer.mozilla.org/en-US/docs/Web/API/RsaPssParams
 */
export function CalculateMaxSaltLength(keySizeInBits: number, digestSizeInBytes: number) {
    return Math.ceil((keySizeInBits - 1) / 8) - digestSizeInBytes - 2;
}

export async function ExportCryptoPublicKey(
    key: CryptoKey,
    header = Strings_BEGIN_PUBLIC_KEY + Strings_LF,
    footer = Strings_LF + Strings_END_PUBLIC_KEY,
) {
    const exported = await crypto.subtle.exportKey(
        "spki",
        key,
    );
    const exportedAsString = ArrayBuffer2String(exported);
    const exportedAsBase64 = btoa(exportedAsString);
    const pemExported = `${header}${exportedAsBase64}${footer}`;

    return pemExported;
}

export async function ExportCryptoPrivateKey(
    key: CryptoKey,
    header = Strings_BEGIN_PRIVATE_KEY + Strings_LF,
    footer = Strings_LF + Strings_END_PRIVATE_KEY,
) {
    const exported = await crypto.subtle.exportKey(
        "pkcs8",
        key,
    );
    const exportedAsString = ArrayBuffer2String(exported);
    const exportedAsBase64 = btoa(exportedAsString);
    const pemExported = `${header}${exportedAsBase64}${footer}`;

    return pemExported;
}

export async function ImportCryptoPublicKey(pem: string, isForEncrypt = true) {
    // fetch the part of the PEM string between header and footer
    const pemHeader = Strings_BEGIN_PUBLIC_KEY;
    const pemFooter = Strings_END_PUBLIC_KEY;
    const pemContents = pem.substring(pemHeader.length, pem.length - pemFooter.length);
    // base64 decode the string to get the binary data
    const binaryDerString = atob(pemContents);
    // convert from a binary string to an ArrayBuffer
    const binaryDer = String2ArrayBuffer(binaryDerString);

    const key = await crypto.subtle.importKey(
        "spki",
        binaryDer,
        {
            name: isForEncrypt ? "RSA-OAEP" : "RSA-PSS",
            hash: "SHA-256",
        },
        true,
        [isForEncrypt ? "encrypt" : "verify"],
    );

    return key;
}

export async function ImportCryptoPrivateKey(pem: string, isForSign = true) {
    // fetch the part of the PEM string between header and footer
    const pemHeader = Strings_BEGIN_PRIVATE_KEY;
    const pemFooter = Strings_END_PRIVATE_KEY;
    const pemContents = pem.substring(pemHeader.length, pem.length - pemFooter.length);
    // base64 decode the string to get the binary data
    const binaryDerString = atob(pemContents);
    // convert from a binary string to an ArrayBuffer
    const binaryDer = String2ArrayBuffer(binaryDerString);

    const key = await crypto.subtle.importKey(
        "pkcs8",
        binaryDer,
        {
            name: isForSign ? "RSA-PSS" : "RSA-OAEP",
            hash: "SHA-256",
        },
        true,
        [isForSign ? "sign" : "decrypt"],
    );

    return key;
}
